import React, { useState, useEffect, useMemo } from 'react';
import { DillButton, DillModal } from '../../../general/Dill';
import { CreditApplication } from '@dill/dill-shared';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import { updateSupplierCreditApplication } from '../../../../redux/services/creditApplicationsService';
import { openDillToast } from '../../../../utils/helpers';
import SpecificRulesSelector, { buildFieldNameMap, SpecificRule } from './SpecificRulesSelector';

const describeRuleChange = (before?: SpecificRule, after?: SpecificRule): string | null => {
  const parts: string[] = [];

  if ((before?.isRequired ?? undefined) !== (after?.isRequired ?? undefined)) {
    parts.push(
      after?.isRequired === undefined
        ? 'requirement reset to default'
        : after.isRequired
        ? 'marked required'
        : 'marked optional'
    );
  }
  if ((before?.hidden ?? undefined) !== (after?.hidden ?? undefined)) {
    parts.push(
      after?.hidden === undefined
        ? 'visibility reset to default'
        : after.hidden
        ? 'hidden'
        : 'forced visible'
    );
  }
  if (
    (before?.minimumNumberOfSignatures ?? undefined) !==
    (after?.minimumNumberOfSignatures ?? undefined)
  ) {
    parts.push(
      after?.minimumNumberOfSignatures === undefined
        ? 'minimum signatures reset to default'
        : `minimum signatures set to ${after.minimumNumberOfSignatures}`
    );
  }
  if (
    (before?.minimumNumberOfGroups ?? undefined) !== (after?.minimumNumberOfGroups ?? undefined)
  ) {
    parts.push(
      after?.minimumNumberOfGroups === undefined
        ? 'minimum count reset to default'
        : `minimum count set to ${after.minimumNumberOfGroups}`
    );
  }
  if ((before?.minimumCreditAmount ?? undefined) !== (after?.minimumCreditAmount ?? undefined)) {
    parts.push(
      after?.minimumCreditAmount === undefined
        ? 'minimum credit amount reset to default'
        : `minimum credit amount set to $${after.minimumCreditAmount}`
    );
  }

  return parts.length > 0 ? parts.join(', ') : null;
};

const ChangeDraftRequirementsModal = ({
  open,
  handleClose,
  creditApplication,
}: {
  open: boolean;
  handleClose: () => void;
  creditApplication: CreditApplication | null;
}) => {
  const dispatch = useAppDispatch();
  const { currentSupplier } = useAppSelector((state) => state.auth);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );

  const [submitting, setSubmitting] = useState(false);
  const [specificRules, setSpecificRules] = useState<SpecificRule[]>([]);
  const [initialSpecificRules, setInitialSpecificRules] = useState<SpecificRule[]>([]);

  const templateForApplication = useMemo(() => {
    if (!creditApplication?.templateId) return null;
    return creditApplicationTemplates.find((t) => t.id === creditApplication.templateId) ?? null;
  }, [creditApplication, creditApplicationTemplates]);

  useEffect(() => {
    if (open) {
      const existingRules = creditApplication?.specificRules ?? [];
      setSpecificRules(existingRules);
      setInitialSpecificRules(existingRules);
    }
  }, [open]);

  const hasChanges = JSON.stringify(specificRules) !== JSON.stringify(initialSpecificRules);

  const changeSummaryLines = useMemo(() => {
    if (!hasChanges || !templateForApplication) return [];
    const fieldNameById = buildFieldNameMap([templateForApplication]);
    const beforeById = new Map(initialSpecificRules.map((r) => [r.fieldId, r]));
    const afterById = new Map(specificRules.map((r) => [r.fieldId, r]));
    const allFieldIds = new Set([...beforeById.keys(), ...afterById.keys()]);

    const lines: string[] = [];
    allFieldIds.forEach((fieldId) => {
      const change = describeRuleChange(beforeById.get(fieldId), afterById.get(fieldId));
      if (change) lines.push(`${fieldNameById.get(fieldId) ?? fieldId}: ${change}`);
    });
    return lines;
  }, [hasChanges, templateForApplication, initialSpecificRules, specificRules]);

  const handleCloseModal = () => {
    setSpecificRules([]);
    setInitialSpecificRules([]);
    setSubmitting(false);
    handleClose();
  };

  const handleSubmit = async () => {
    if (!creditApplication) return;
    if (!currentSupplier?.id) return;
    setSubmitting(true);

    const results = await dispatch(
      updateSupplierCreditApplication({
        supplierId: currentSupplier?.id || '',
        creditApplicationId: creditApplication.id,
        specificRules,
        ...(changeSummaryLines.length > 0 && { comment: changeSummaryLines.join('\n') }),
      })
    );

    if (results.type.endsWith('/fulfilled')) {
      openDillToast({ type: 'SUCCESS', message: 'Requirements updated successfully.' });
      handleCloseModal();
    } else {
      openDillToast({ type: 'ERROR', message: 'Failed to update requirements.' });
    }
    setSubmitting(false);
  };

  return (
    <DillModal
      open={open}
      onClose={handleCloseModal}
      title="Change Requirements"
      maxWidth="620px"
      footer={
        <div className="flex items-center justify-end w-full gap-2">
          <DillButton label="Cancel" variant="secondary" onClick={handleCloseModal} />
          <DillButton
            label="Update Requirements"
            onClick={handleSubmit}
            isLoading={submitting}
            isDisabled={!creditApplication || submitting || !hasChanges}
          />
        </div>
      }>
      <div className="flex flex-col gap-5">
        {templateForApplication ? (
          <SpecificRulesSelector
            templates={[templateForApplication]}
            rules={specificRules}
            onChange={setSpecificRules}
            alwaysOpen
          />
        ) : (
          <p className="text-sm text-gray-500">
            No template is associated with this application. Requirements cannot be changed.
          </p>
        )}
      </div>
    </DillModal>
  );
};

export default ChangeDraftRequirementsModal;
